import {
  API_URL,
  CMR_AMBALAJE_LIST,
  CMR_DESCARCARI_LIST,
  TRAILERS_L,
  TRUCKS_L,
  formatDate,
  formatDestinatar,
  formatName,
  isExpiredJWT,
  rutaFilter,
} from "../utils";
import { Controller, useForm } from "react-hook-form";
import {
  Button,
  Checkbox,
  MultiSelect,
  NumberInput,
  Select,
  TextInput,
} from "@mantine/core";
import axios from "axios";
import { useContext, useEffect, useState } from "react";
import { AuthContext } from "../context/AuthContext";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import useFeedback from "../hooks/useFeedback";
import useRute from "../hooks/useRute";

function ShowCmr() {
  const { auth, refresh } = useContext(AuthContext);
  const { cmrId } = useParams();
  const [cmr, setCmr] = useState(null);

  useEffect(() => {
    async function getCmr() {
      try {
        const res = await axios.get(API_URL + `/api/l/cmr/${cmrId}`, {
          headers: { Authorization: `Bearer ${auth.aT}` },
        });

        setCmr(res.data);
      } catch (err) {
        console.error(err);
      }
    }

    if (auth.aT && !isExpiredJWT(auth.user.exp)) getCmr();
    else refresh();
  }, [auth, refresh, cmrId]);

  return (
    <div>
      <h1 className="text-2xl font-bold mb-2">Actualizare CMR</h1>
      {cmr && <UpdateCmr cmr={cmr} />}
    </div>
  );
}

function UpdateCmr({ cmr }) {
  const { auth, refresh } = useContext(AuthContext);
  const { state } = useLocation();
  const isAdmin = auth.user.permissions.includes("Admin");
  const rute = useRute();
  const ruteArr = isAdmin ? rute.map((ruta) => ruta.number) : rute;
  const setFeedback = useFeedback();
  const navigate = useNavigate();

  const errorCfg = {
    truck: { func: (v) => TRUCKS_L.includes(v), msg: "Numar camion invalid" },

    trailer: {
      func: (v) => TRAILERS_L.includes(v),
      msg: "Numar remorca invalid",
    },
    ambalaje: {
      func: (v) => CMR_AMBALAJE_LIST.includes(v),
      msg: "Valoare ambalaje invalida",
    },
    locul_descarcarii: {
      func: (v) => CMR_DESCARCARI_LIST.includes(v),
      msg: "Loc descarcare invalid",
    },
    destinatar: {
      func1: (arr) => arr.every((val) => ruteArr.includes(val)),
      msg1: "Valoare destinatar invalida",
      func2: (arr) => arr.length > 0,
      msg2: "Campul este obligatoriu",
    },
    greutate: { func: (v) => v > 0, msg: "Numar greutate invalid" },
    numar_paleti: { func: (v) => v > 0, msg: "Numar paleti invalid" },
    documente: { func: (v) => v > 0, msg: "Numar TLB invalid" },
  };

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      date: formatDate(cmr.date),
      added_by_user: formatName(cmr.added_by_user.name),
      destinatar: formatDestinatar(cmr.destinatar),
      locul_descarcarii: cmr.locul_descarcarii,
      temperatura_controlata: cmr.temperatura_controlata,
      documente: cmr.documente.split(" ")[1],
      ambalaje: cmr.ambalaje,
      numar_paleti: cmr.numar_paleti,
      greutate: cmr.greutate,
      instructiuni: cmr.instructiuni,
      truck: cmr.truck,
      trailer: cmr.trailer,
    },
  });

  async function handleUpdate(data) {
    data.added_by_user = cmr.added_by_user._id;
    data.date = cmr.date;
    data.destinatar = "LIDL DISCOUNT FILIALA " + data.destinatar.join(", ");
    data.documente = "TLB " + String(data.documente);
    const aT = isExpiredJWT(auth.user.exp) ? await refresh() : auth.aT;
    try {
      await axios.post(API_URL + `/api/l/cmr/${cmr.id}/update`, data, {
        headers: { Authorization: `Bearer ${aT}` },
      });
      setFeedback({
        type: "Success",
        active: true,
        msg: "CMR actualizat cu succes.",
      });
      navigate("/l/cmr/list", { state });
    } catch (err) {
      console.error(err);
      setFeedback({
        type: "Error",
        active: true,
        msg: "CMR nu a putut fi actualizat.",
      });
    }
  }

  async function handleDelete() {
    if (window.confirm("Confirma STERGERE CMR.")) {
      const aT = isExpiredJWT(auth.user.exp) ? await refresh() : auth.aT;
      try {
        await axios.post(
          API_URL + `/api/l/cmr/${cmr.id}/delete`,
          {},
          { headers: { Authorization: `Bearer ${aT}` } }
        );
        setFeedback({
          type: "Success",
          active: true,
          msg: "CMR a fost sters cu succes.",
        });
        navigate("/l/cmr/list", { state });
      } catch (err) {
        console.error(err);
        setFeedback({
          type: "Error",
          active: true,
          msg: "CMR nu a putut fi sters.",
        });
      }
    }
  }

  return (
    <form
      onSubmit={handleSubmit(handleUpdate)}
      className="max-w-[400px] flex flex-col gap-2"
    >
      <Controller
        name="date"
        control={control}
        render={({ field }) => (
          <TextInput {...field} disabled label="Data" withAsterisk />
        )}
      />
      <Controller
        name="added_by_user"
        control={control}
        render={({ field }) => (
          <TextInput {...field} disabled label="Utilizator" withAsterisk />
        )}
      />
      <Controller
        name="truck"
        control={control}
        rules={{
          validate: (val) => errorCfg.truck.func(val) || errorCfg.truck.msg,
        }}
        render={({ field }) => (
          <Select
            {...field}
            label="Camion"
            placeholder="Selecteaza un camion..."
            nothingFoundMessage="Camion inexistent"
            withAsterisk
            searchable
            data={TRUCKS_L}
          />
        )}
      />
      <Controller
        name="trailer"
        control={control}
        rules={{
          validate: (val) => errorCfg.trailer.func(val) || errorCfg.trailer.msg,
        }}
        render={({ field }) => (
          <Select
            {...field}
            label="Remorca"
            placeholder="Selecteaza o remorca..."
            nothingFoundMessage="Remorca inexistenta"
            withAsterisk
            searchable
            data={TRAILERS_L}
          />
        )}
      />
      <Controller
        name="locul_descarcarii"
        control={control}
        rules={{
          validate: (val) =>
            errorCfg.locul_descarcarii.func(val) ||
            errorCfg.locul_descarcarii.msg,
        }}
        render={({ field }) => (
          <Select
            {...field}
            label="Locul descarcarii"
            placeholder="Selecteaza locul descarcarii..."
            nothingFoundMessage="Loc descarcare invalid"
            withAsterisk
            searchable
            data={CMR_DESCARCARI_LIST}
          />
        )}
      />
      <Controller
        name="destinatar"
        control={control}
        rules={{
          validate: {
            validValue: (val) =>
              errorCfg.destinatar.func1(val) || errorCfg.destinatar.msg1,
            notEmpty: (val) =>
              errorCfg.destinatar.func2(val) || errorCfg.destinatar.msg2,
          },
        }}
        render={({ field }) => (
          <MultiSelect
            {...field}
            label="Destinatar"
            error={errors.destinatar ? errors.destinatar.message : ""}
            placeholder="Adauga destinatar..."
            data={ruteArr}
            searchable
            filter={rutaFilter}
            required
            clearable
            nothingFoundMessage="Destinatar invalid..."
          />
        )}
      />
      <Controller
        name="ambalaje"
        control={control}
        rules={{
          validate: (v) => errorCfg.ambalaje.func(v) || errorCfg.ambalaje.msg,
        }}
        render={({ field }) => (
          <Select
            {...field}
            label="Ambalaje"
            placeholder="Selecteaza ambalaje..."
            nothingFoundMessage="Valoare inexistenta"
            withAsterisk
            searchable
            data={CMR_AMBALAJE_LIST}
          />
        )}
      />
      <Controller
        name="greutate"
        control={control}
        rules={{
          validate: (v) => errorCfg.greutate.func(v) || errorCfg.greutate.msg,
        }}
        render={({ field }) => (
          <NumberInput
            {...field}
            label="Greutate"
            error={errors.greutate ? errors.greutate.message : ""}
            placeholder="Greutate..."
            min={1}
            withAsterisk
          />
        )}
      />
      <Controller
        name="numar_paleti"
        control={control}
        rules={{
          validate: (v) =>
            errorCfg.numar_paleti.func(v) || errorCfg.numar_paleti.msg,
        }}
        render={({ field }) => (
          <NumberInput
            {...field}
            label="Numar paleti"
            error={errors.numar_paleti ? errors.numar_paleti.message : ""}
            placeholder="Numar paleti..."
            min={1}
            withAsterisk
          />
        )}
      />
      <Controller
        name="documente"
        control={control}
        rules={{
          validate: (v) => errorCfg.documente.func(v) || errorCfg.documente.msg,
        }}
        render={({ field }) => (
          <NumberInput
            {...field}
            label="TLB"
            error={errors.documente ? errors.documente.message : ""}
            placeholder="Numar TLB..."
            min={1}
            withAsterisk
          />
        )}
      />
      <Controller
        name="instructiuni"
        control={control}
        render={({ field }) => (
          <TextInput
            {...field}
            label="Instructiuni"
            placeholder="Instructiuni..."
          />
        )}
      />

      <Controller
        name="temperatura_controlata"
        control={control}
        render={({ field }) => (
          <Checkbox {...field} label="Temperatura controlata" />
        )}
      />

      <div className="flex justify-start gap-3 mt-2">
        <Button
          type="submit"
          color="green"
          onClick={handleSubmit(handleUpdate)}
        >
          Actualizeaza
        </Button>
        <Button color="red" onClick={handleSubmit(handleDelete)}>
          Sterge
        </Button>
      </div>
    </form>
  );
}

export default ShowCmr;
